import { useTranslation } from "react-i18next";

import { Mascot } from "../components/Mascot";
import { MASCOTS } from "../components/mascots";
import { useHasKey } from "../components/useHasKey";

/**
 * Every limitation the page is willing to state, in the order it states them.
 *
 * This is a superset, not a fixed list: entries disappear from the dictionary as the product
 * changes state, and the section renders whatever is actually defined. Hardcoding six here
 * would leave an empty cell baked into the pre-rendered HTML the day one of them is dropped.
 */
const LIMITS = [
  "metadata",
  "push",
  "audit",
  "history",
  "recovery",
  "logs",
  "prerelease",
] as const;

/**
 * The section the old site did not have, and the reason this page is worth reading.
 *
 * Tinted with the accent instead of being set plainly, and this is the one place on the page
 * where emphasis is spent on a downside: a limitation styled to be skimmed past reads as one
 * we tried to bury. Deliberately not an accordion and not set smaller.
 */
export function Limits() {
  const { t } = useTranslation();
  const has = useHasKey();

  const limits = LIMITS.filter((limit) => has(`limits.${limit}.title`));

  // No entries left means no section at all, rather than a heading over nothing.
  if (limits.length === 0) return null;

  return (
    <section className="section" id="limits">
      <div className="section__intro">
        <div className="section__head">
          <span className="section__eyebrow">06</span>
          <h2 className="section__heading">{t("limits.heading")}</h2>
          {has("limits.lede") ? <p className="section__lede">{t("limits.lede")}</p> : null}
        </div>

        {/* The one unsmiling frame of the set — paw under the chin, brows down — and the
            reason it is kept for here: this section is the page's uncomfortable half, and any
            of the other four would read as a smirk. */}
        <Mascot mascot={MASCOTS.thinking} />
      </div>

      {/* Two columns, never three: the list is meant to come out even at four entries as
          well as at six, and a lone cell on a third column reads like an afterthought. */}
      <ul className="limits">
        {limits.map((limit) => (
          <li className="limit" key={limit}>
            <div>
              <h3 className="limit__title">{t(`limits.${limit}.title`)}</h3>
              <p className="limit__body">{t(`limits.${limit}.body`)}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
